import { ExportButton } from "@components/exports/ExportButton";
import { EXPORT_LONGLIST_EXECUTIVE_SUMMARY_TO_GOOGLE_DOCS } from "@lib/graphql/mutations";
import { LONGLIST_EXECUTIVE_SUMMARY_QUERY } from "@lib/graphql/queries";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router";
import remarkGfm from "remark-gfm";
import { useMutation, useQuery } from "urql";

interface LonglistExecutiveSummaryData {
  campaignId: string;
  campaignTitle: string;
  markdown: string;
}

export function LonglistExecutiveSummaryPage() {
  const { id } = useParams();
  const [{ data, fetching, error }] = useQuery<{
    longlistExecutiveSummary: LonglistExecutiveSummaryData | null;
  }>({
    query: LONGLIST_EXECUTIVE_SUMMARY_QUERY,
    variables: { campaignId: id },
  });

  const [, exportToGoogleDocs] = useMutation(EXPORT_LONGLIST_EXECUTIVE_SUMMARY_TO_GOOGLE_DOCS);

  const startExport = async () => {
    const result = await exportToGoogleDocs({ input: { campaignId: id } });
    const payload = result.data?.exportLonglistExecutiveSummaryToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  const summary = data?.longlistExecutiveSummary;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <Link
          to={`/campaigns/${id}/overview`}
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-secondary"
        >
          <ArrowLeft size={14} /> Back to campaign
        </Link>
        {summary && <ExportButton onStart={startExport} />}
      </div>

      {fetching && !summary && (
        <p className="text-sm text-text-muted">Loading executive summary…</p>
      )}

      {error && (
        <p className="text-sm text-red-600" data-summary-error>
          Failed to load summary: {error.message}
        </p>
      )}

      {!fetching && !error && !summary && (
        <p className="text-sm text-text-muted">Campaign not found.</p>
      )}

      {summary && (
        <article
          className="prose prose-sm max-w-none rounded-lg bg-card p-6 shadow-sm ring-1 ring-ring dark:prose-invert"
          data-longlist-executive-summary
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary.markdown}</ReactMarkdown>
        </article>
      )}
    </div>
  );
}
